// src/pages/ChoreographyDetail.js - one choreography of the leader's group.
//
// Audit fixes: FL-04 (errors of every action are shown), FL-05 (dancers and music are
// read-only once presence is confirmed or the competition is locked), FL-07 (load
// errors have a working retry, action errors never hide the page), FL-13/FL-19
// (StatusBadge and readable category labels), FL-18 (dancer editing with the same
// picker and rules as the wizard), FL-20 (competition context, deadline, "Co zbývá"
// checklist, sticky next step on phones), FL-31 (verification result), FL-29
// (dialogs instead of window.confirm).
// Online presence is due a week before the competition (shown in the header and the sticky
// bar, not enforced). An error of "Zaregistrovat" / "Potvrdit prezenci" stays inside the dialog.
// The verification video checks the discipline only.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, Check, Edit, Trash2, Users, Trophy, Mail } from 'lucide-react';
import {
  choreographiesApi,
  competitionsApi,
  dancersApi,
  getErrorMessage,
} from '../services/api';
import { categoryLabel, CHOREOGRAPHY_STATUS, ageCategoryLabel } from '../constants/categories';
import {
  formatDate,
  formatDateLong,
  countLabel,
  isPastCompetition,
  isRegistrationClosed,
  presenceDeadline,
} from '../utils/format';
import useFetch from '../hooks/useFetch';
import { Alert, Button, ConfirmDialog, Modal, PageHeader, Spinner, StatusBadge, useToast } from '../components/ui';
import BackButton from '../components/layout/BackButton';
import MusicUploadSection from '../components/layout/MusicUploadSection';
import VideoUploadSection from '../components/layout/VideoUploadSection';
import BackgroundVideoUploader from '../components/layout/BackgroundVideoUploader';
import DancerPicker, { evaluateSelection, MIN_DANCERS, MAX_DANCERS, yearsLabel } from '../components/leader/DancerPicker';
import { useMeta } from '../contexts/MetaContext';
import ChoreographyChecklist from '../components/leader/ChoreographyChecklist';
import TextField from '../components/leader/TextField';
import LoadError from '../components/leader/LoadError';
import { DeadlineText } from '../components/leader/CompetitionBadges';

const EDITABLE_STATUSES = ['pre_registered', 'draft'];
const LOCKED_COMPETITION = ['finished', 'archived'];
const CONTACT_EMAIL = 'info@bestofthebest.cz';
const DANCER_FORMS = ['tanečník', 'tanečníci', 'tanečníků'];

const LOCKED_AFTER_PRESENCE =
  `Po potvrzení prezence už nelze měnit hudbu ani tanečníky. Potřebujete změnu? Napište organizátorům na ${CONTACT_EMAIL}.`;
const LOCKED_REJECTED = `Choreografie byla zamítnuta – kontaktujte prosím organizátora na ${CONTACT_EMAIL}.`;
const LOCKED_COMPETITION_TEXT = 'Soutěž je ukončená – choreografii už nelze měnit.';

// The organiser checks the discipline from the video, not the league (bestofthebest.cz).
function VerificationResult({ status, note }) {
  if (status === 'approved') {
    return (
      <Alert tone="success" title="Video zkontrolováno">
        Organizátor zkontroloval video: disciplína odpovídá.
        {note && <p className="mt-1">Poznámka organizátora: {note}</p>}
      </Alert>
    );
  }
  if (status === 'change_needed') {
    return (
      <Alert tone="warning" title="Organizátor navrhuje změnu kategorie">
        <p>Organizátor podle videa navrhuje změnu zařazení (obvykle disciplíny){note ? ' – viz poznámka' : ''}.</p>
        {note && <p className="mt-1">Poznámka organizátora: {note}</p>}
        <p className="mt-1">
          Napište nám prosím na{' '}
          <a className="font-medium underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Alert>
    );
  }
  if (note) {
    return <Alert tone="info" title="Poznámka organizátora">{note}</Alert>;
  }
  return null;
}

function presenceBlockers(choreography) {
  const reasons = [];
  if (!choreography.musicFile) reasons.push('nahrajte hudbu');
  const count = (choreography.dancers || []).length;
  if (count < MIN_DANCERS) reasons.push(`vyberte alespoň ${MIN_DANCERS} tanečníky`);
  if (count > MAX_DANCERS) reasons.push(`snižte počet tanečníků na nejvýše ${MAX_DANCERS}`);
  return reasons;
}

const ChoreographyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const meta = useMeta();

  const { data, error, isLoading, reload } = useFetch(async () => {
    const [choreographyRes, dancersRes] = await Promise.all([choreographiesApi.getById(id), dancersApi.getAll()]);
    return { choreography: choreographyRes.data, dancers: dancersRes.data || [] };
  }, id);

  const choreography = data?.choreography;
  const groupDancers = data?.dancers || [];
  const competitionId = choreography?.competition?.id;

  // Registration window of the competition (deadline, opening date) - optional extra.
  const [competition, setCompetition] = useState(null);
  useEffect(() => {
    if (!competitionId) return undefined;
    let cancelled = false;
    competitionsApi
      .getById(competitionId)
      .then((res) => {
        if (!cancelled) setCompetition(res.data);
      })
      .catch(() => {
        if (!cancelled) setCompetition(null);
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  const [dialog, setDialog] = useState(null); // register | presence | delete | dancers | edit
  const [dialogError, setDialogError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedDancers, setSelectedDancers] = useState([]);
  const [form, setForm] = useState({ name: '', choreographer: '' });

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);

  const refresh = useCallback(() => reload({ silent: true }), [reload]);

  if (isLoading && !choreography) return <Spinner label="Načítám choreografii…" />;
  if (error && !choreography) {
    return <LoadError title="Choreografii se nepodařilo načíst" error={error} onRetry={reload} />;
  }
  if (!choreography) return null;

  const status = choreography.status;
  const competitionInfo = { ...(choreography.competition || {}), ...(competition || {}) };
  // Locked when finished/archived or simply over (old rounds often keep their status).
  const competitionLocked = LOCKED_COMPETITION.includes(competitionInfo.status) || isPastCompetition(competitionInfo);
  const editable = EDITABLE_STATUSES.includes(status) && !competitionLocked;
  const isFinale = competitionInfo.type === 'grand_finale' || competitionInfo.isGrandFinale;
  const isFree = Boolean(competitionInfo.isFree);
  const dancers = choreography.dancers || [];
  // After the registration deadline a pre-registration can no longer be registered (the server refuses it).
  const registrationClosed = isRegistrationClosed(competitionInfo);
  const canRegister =
    status === 'pre_registered' &&
    !registrationClosed &&
    (choreography.canRegister !== undefined ? choreography.canRegister : competitionInfo.status === 'active');
  const blockers = presenceBlockers(choreography);
  const presenceDue = presenceDeadline(competitionInfo);
  const lockedReason = competitionLocked
    ? LOCKED_COMPETITION_TEXT
    : status === 'rejected'
    ? LOCKED_REJECTED
    : LOCKED_AFTER_PRESENCE;
  const selection = evaluateSelection(groupDancers, selectedDancers, choreography.ageCategory, competitionInfo.date, meta.ageRules);
  const averageAge = dancers.length
    ? Math.round(dancers.reduce((sum, d) => sum + (Number(d.age) || 0), 0) / dancers.length)
    : null;

  // --- actions ------------------------------------------------------------------------
  // The dialog stays open on failure and shows the server's reason inside it.
  const runAction = async (request, successMessage) => {
    setBusy(true);
    setDialogError(null);
    try {
      await request();
      setDialog(null);
      toast.success(successMessage);
      await refresh();
    } catch (err) {
      setDialogError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openDialog = (name) => {
    setDialogError(null);
    setDialog(name);
  };

  const handleRegister = () =>
    runAction(
      () => choreographiesApi.register(id),
      'Choreografie je zaregistrovaná. Teď nahrajte hudbu a potvrďte prezenci.'
    );

  const handleConfirmPresence = () =>
    runAction(() => choreographiesApi.confirmPresence(id), 'Prezence je potvrzená. Děkujeme!');

  const handleDelete = async () => {
    setBusy(true);
    setDialogError(null);
    try {
      await choreographiesApi.delete(id);
      toast.success(`Choreografie „${choreography.name}“ byla smazána.`);
      navigate('/choreographies', { replace: true });
    } catch (err) {
      setDialogError(getErrorMessage(err));
      setBusy(false);
    }
  };

  const openDancers = () => {
    setSelectedDancers(dancers.map((d) => d.id));
    setDialogError(null);
    setDialog('dancers');
  };

  const handleSaveDancers = async () => {
    setBusy(true);
    setDialogError(null);
    try {
      await choreographiesApi.updateDancers(id, selectedDancers);
      setDialog(null);
      toast.success('Seznam tanečníků byl uložen.');
      await refresh();
    } catch (err) {
      setDialogError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    setForm({ name: choreography.name || '', choreographer: choreography.choreographer || '' });
    setDialogError(null);
    setDialog('edit');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const choreographer = form.choreographer.trim();
    if (!name || !choreographer) {
      setDialogError('Vyplňte prosím název choreografie i choreografa.');
      return;
    }
    setBusy(true);
    setDialogError(null);
    try {
      await choreographiesApi.update(id, { name, choreographer });
      setDialog(null);
      toast.success('Změny byly uloženy.');
      await refresh();
    } catch (err) {
      setDialogError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // --- next step (sticky at the bottom on phones) ---------------------------------------
  let nextStep = null;
  if (!competitionLocked && status === 'pre_registered') {
    if (canRegister) {
      nextStep = (
        <>
          <p className="text-sm text-gray-700">
            Registrace je otevřená. Zaregistrujte choreografii – teprve pak se počítá do soutěže.
          </p>
          <Button className="w-full sm:w-auto" onClick={() => openDialog('register')} disabled={busy}>
            <Check className="h-5 w-5" aria-hidden="true" />
            Registrovat choreografii
          </Button>
        </>
      );
    } else if (competitionInfo.status === 'upcoming' && !registrationClosed) {
      nextStep = (
        <p className="text-sm text-gray-700">
          Registrace na tuto soutěž ještě není otevřená
          {competitionInfo.registrationStart ? ` (otevře se ${formatDate(competitionInfo.registrationStart)})` : ''}. Potom se
          sem vraťte a choreografii zaregistrujte.
        </p>
      );
    } else {
      nextStep = (
        <p className="text-sm text-gray-700">
          Registrace na tuto soutěž je uzavřena
          {competitionInfo.registrationEnd ? ` (uzávěrka ${formatDate(competitionInfo.registrationEnd)})` : ''} – choreografii
          již nelze zaregistrovat.
        </p>
      );
    }
  } else if (!competitionLocked && status === 'draft') {
    nextStep = (
      <>
        <div className="space-y-1 text-sm text-gray-700">
          <p>
            {blockers.length > 0
              ? `Pro potvrzení prezence ještě ${blockers.join(' a ')}.`
              : 'Vše je připraveno. Potvrzením prezence závazně potvrzujete účast a finální hudbu i tanečníky.'}
          </p>
          {presenceDue && (
            <p className="font-medium text-gray-900">
              Online prezenci potvrďte nejpozději do {formatDate(presenceDue)} (týden před soutěží).
            </p>
          )}
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => openDialog('presence')}
          disabled={blockers.length > 0 || busy}
        >
          <Check className="h-5 w-5" aria-hidden="true" />
          Potvrdit online prezenci
        </Button>
      </>
    );
  }

  const statusMeta = CHOREOGRAPHY_STATUS[status];

  return (
    <div className={`space-y-6 ${nextStep ? 'pb-4' : ''}`}>
      <BackButton to="/choreographies" label="Zpět na choreografie" />

      <PageHeader
        title={choreography.name}
        subtitle={categoryLabel(choreography.discipline, choreography.ageCategory, choreography.league)}
        actions={
          editable ? (
            <>
              <Button variant="secondary" onClick={openEdit}>
                <Edit className="h-4 w-4" aria-hidden="true" />
                Upravit
              </Button>
              <Button variant="secondary" onClick={() => openDialog('delete')}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Smazat
              </Button>
            </>
          ) : null
        }
      >
        <div className="mt-3 space-y-2 text-sm text-gray-700">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="choreography" status={status} />
            {statusMeta?.hint && <span className="text-gray-600">{statusMeta.hint}</span>}
          </div>
          {choreography.choreographer && <p>Choreograf/ka: {choreography.choreographer}</p>}
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1.5">
              {isFinale ? (
                <Trophy className="h-4 w-4 text-amber-600" aria-hidden="true" />
              ) : (
                <CalendarDays className="h-4 w-4 text-gray-500" aria-hidden="true" />
              )}
              Soutěž:{' '}
              {competitionInfo.id ? (
                <Link to={`/competitions/${competitionInfo.id}`} className="font-medium text-indigo-700 hover:text-indigo-900">
                  {competitionInfo.name}
                </Link>
              ) : (
                competitionInfo.name
              )}
              {competitionInfo.date && <span>· {formatDateLong(competitionInfo.date)}</span>}
            </span>
            {competitionInfo.registrationEnd && !competitionLocked && !registrationClosed && (
              <DeadlineText date={competitionInfo.registrationEnd} />
            )}
            {presenceDue && !competitionLocked && EDITABLE_STATUSES.includes(status) && (
              <DeadlineText date={presenceDue} label="Online prezence do" />
            )}
          </p>
        </div>
      </PageHeader>

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="todo-title">
        <h2 id="todo-title" className="mb-3 text-lg font-semibold text-gray-900">
          Co zbývá
        </h2>
        <ChoreographyChecklist choreography={choreography} />
        {choreography.earlyBird?.eligible && choreography.earlyBird?.position && (
          <p className="mt-3 text-sm text-gray-600">
            Pořadí pro zvýhodněné startovné: {choreography.earlyBird.position}.{' '}
            {choreography.earlyBird.isEarlyBird
              ? 'Máte nárok na zvýhodněné startovné.'
              : `Zvýhodněné startovné platí pro prvních ${choreography.earlyBird.earlyBirdSpots} choreografií.`}
          </p>
        )}
        {status === 'rejected' && (
          <p className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <Mail className="h-4 w-4" aria-hidden="true" />
            Kontaktujte prosím organizátory na{' '}
            <a className="font-medium text-indigo-700 underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
        )}
        {competitionLocked && <p className="mt-3 text-sm text-gray-700">{LOCKED_COMPETITION_TEXT}</p>}
      </section>

      <VerificationResult status={choreography.verification_status} note={choreography.verification_note} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <MusicUploadSection
          choreographyId={choreography.id}
          musicFile={choreography.musicFile}
          onMusicUploaded={refresh}
          editable={editable}
          lockedReason={lockedReason}
        />
        {isFinale ? (
          <BackgroundVideoUploader
            choreographyId={choreography.id}
            backgroundVideo={choreography.finaleBackgroundVideo}
            onVideoUploaded={refresh}
            editable={!competitionLocked}
            lockedReason={LOCKED_COMPETITION_TEXT}
            competitionDate={competitionInfo.date}
          />
        ) : (
          <VideoUploadSection
            choreographyId={choreography.id}
            verificationVideo={choreography.verificationVideo}
            onVideoUploaded={refresh}
            editable={!competitionLocked}
            lockedReason={LOCKED_COMPETITION_TEXT}
          />
        )}
      </div>

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="dancers-title">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="dancers-title" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Users className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              Tanečníci ({dancers.length})
            </h2>
            {averageAge !== null && (
              <p className="text-sm text-gray-600">Průměrný věk v roce soutěže: {yearsLabel(averageAge)}</p>
            )}
          </div>
          {editable && (
            <Button variant="secondary" onClick={openDancers}>
              <Users className="h-4 w-4" aria-hidden="true" />
              Upravit seznam tanečníků
            </Button>
          )}
        </div>
        {!editable && <p className="mb-4 text-sm text-gray-600">{lockedReason}</p>}
        {dancers.length === 0 ? (
          <p className="text-sm text-gray-600">V choreografii zatím nejsou žádní tanečníci.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {dancers.map((dancer) => (
              <li key={dancer.id} className="rounded-md border border-gray-200 p-3">
                <p className="font-medium text-gray-900">
                  {dancer.firstName} {dancer.lastName}
                </p>
                <p className="text-sm text-gray-600">
                  {formatDate(dancer.birthDate)} · {yearsLabel(dancer.age)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {nextStep && (
        <div className="sticky bottom-0 z-20 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur sm:-mx-6 sm:px-6 md:static md:mx-0 md:rounded-lg md:border md:p-6 md:shadow">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">{nextStep}</div>
        </div>
      )}

      {dialog === 'register' && (
        <ConfirmDialog
          title="Zaregistrovat choreografii?"
          message="Po registraci se choreografie započítá do kapacity soutěže. Potom nahrajte hudbu a potvrďte prezenci."
          confirmLabel="Zaregistrovat"
          loading={busy}
          error={dialogError}
          onConfirm={handleRegister}
          onCancel={closeDialog}
        />
      )}

      {dialog === 'presence' && (
        <ConfirmDialog
          title="Potvrdit online prezenci?"
          confirmLabel="Potvrdit prezenci"
          loading={busy}
          error={dialogError}
          onConfirm={handleConfirmPresence}
          onCancel={closeDialog}
        >
          <p>
            Potvrzením prezence závazně potvrzujete účast choreografie „{choreography.name}“ na soutěži a její finální
            podobu.
          </p>
          <p>
            Zkontrolujte, že je nahraná finální verze hudby.
            {!isFree &&
              ` Potvrzením prezence se zavazujete k úhradě startovného v plné výši podle počtu přihlášených tanečníků (${dancers.length}).`}
          </p>
          <p className="font-medium text-gray-900">
            Po potvrzení už nebude možné měnit hudbu ani seznam tanečníků ({countLabel(dancers.length, DANCER_FORMS)}).
          </p>
        </ConfirmDialog>
      )}

      {dialog === 'delete' && (
        <ConfirmDialog
          title="Smazat choreografii?"
          message={`Opravdu chcete smazat choreografii „${choreography.name}“? Smaže se i nahraná hudba a videa. Tuto akci nelze vrátit.`}
          confirmLabel="Smazat choreografii"
          danger
          loading={busy}
          error={dialogError}
          onConfirm={handleDelete}
          onCancel={closeDialog}
        />
      )}

      {dialog === 'dancers' && (
        <Modal
          title="Upravit seznam tanečníků"
          onClose={busy ? undefined : closeDialog}
          size="xl"
          closeOnBackdrop={false}
          footer={
            <>
              <Button variant="secondary" onClick={closeDialog} disabled={busy}>
                Zrušit
              </Button>
              <Button onClick={handleSaveDancers} loading={busy} disabled={!selection.valid}>
                Uložit
              </Button>
            </>
          }
        >
          <p className="mb-3 text-sm text-gray-600">
            Kategorie {ageCategoryLabel(choreography.ageCategory)} · věk se počítá k roku soutěže
            {competitionInfo.date ? ` (${formatDate(competitionInfo.date)})` : ''}.
          </p>
          {dialogError && (
            <Alert tone="error" className="mb-3">
              {dialogError}
            </Alert>
          )}
          <DancerPicker
            dancers={groupDancers}
            competitionDate={competitionInfo.date}
            ageCategory={choreography.ageCategory}
            value={selectedDancers}
            onChange={setSelectedDancers}
          />
        </Modal>
      )}

      {dialog === 'edit' && (
        <Modal
          title="Upravit choreografii"
          onClose={busy ? undefined : closeDialog}
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={closeDialog} disabled={busy}>
                Zrušit
              </Button>
              <Button type="submit" form="edit-choreography-form" loading={busy}>
                Uložit změny
              </Button>
            </>
          }
        >
          <form id="edit-choreography-form" onSubmit={handleEditSubmit} className="space-y-4" noValidate>
            {dialogError && <Alert tone="error">{dialogError}</Alert>}
            <TextField
              label="Název choreografie"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={100}
              required
              autoComplete="off"
            />
            <TextField
              label="Choreograf/ka nebo choreografové"
              value={form.choreographer}
              onChange={(e) => setForm({ ...form, choreographer: e.target.value })}
              maxLength={255}
              required
              placeholder="např. Jan Novák, Eva Svobodová"
              autoComplete="off"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Kategorie</p>
              <p className="mt-1 rounded-md bg-gray-50 p-2 text-sm text-gray-700">
                {categoryLabel(choreography.discipline, choreography.ageCategory, choreography.league)}
              </p>
              <p className="mt-1 text-xs text-gray-600">
                Disciplínu, věkovou kategorii nebo ligu může změnit jen organizátor – napište na {CONTACT_EMAIL}.
              </p>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ChoreographyDetail;
